import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Box,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { isUndefined } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";

interface Product {
  id?: string;
  unitName: string;
  quantity: number;
  unitPrice: number;
  attachment?: File | null;
  notice?: string;
  image?: { url: string };
}

interface TenderQuotation {
  tender: { title: string; id: string; status: string };
  id: string;
  status: string;
  products: Product[];
  deliverDays: number;
  DeadLineDate: string;
  paymentMethod: string;
  contactMethod: string;
  DeliveryMethod: string;
  company: {
    CompanyNr: string;
    nameAr: string;
    website: string;
    address: string;
  };
}

interface ComparisonModalProps {
  open: boolean;
  onClose: () => void;
  offers: TenderQuotation[];
}

const ComparisonModal: React.FC<ComparisonModalProps> = ({
  open,
  onClose,
  offers,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  // const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const getDateLabel = (days: string) => {
    switch (days) {
      case "7":
        return `7 ${t("time.days")}`;
      case "14":
        return `14 ${t("time.days")}`;
      case "30":
        return `30 ${t("time.days")}`;
      default:
        return `7 ${t("time.days")}`;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle
        align="center"
        sx={{ fontWeight: "bold", fontSize: "1.5rem", pb: 2 }}
      >
        {t("statistics.ComparisonTitle")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          {offers.map((quotation, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={0}
                sx={{
                  padding: 2,
                  borderRadius: "10px",
                  border:
                    index % 2 === 0 ? "2px solid #1976d2" : "2px solid #f57c00",
                }}
              >
                <Typography
                  variant="h6"
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: index % 2 === 0 ? "#1976d2" : "#f57c00",
                    mb: 2,
                  }}
                >
                  {quotation.company?.CompanyNr}
                </Typography>
                <Typography
                  align="center"
                  variant="body2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("orders.fields.status")}: {quotation.status}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 2, textAlign: "center" }}
                >
                  {t("products.list")}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="right">
                          {t("checkout.fields.name")}
                        </TableCell>
                        <TableCell align="right">
                          {t("checkout.fields.quantity")}
                        </TableCell>
                        <TableCell align="right">
                          {t("checkout.fields.unitPrice")}
                        </TableCell>
                        <TableCell align="right">
                          {t("orders.fields.notes")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quotation.products.map((product, idx) => (
                        <TableRow key={idx}>
                          <TableCell align="right">
                            {product.unitName}
                          </TableCell>
                          <TableCell align="right">
                            {product.quantity}
                          </TableCell>
                          <TableCell align="right">
                            {product.unitPrice.toLocaleString("ar-EG")}{" "}
                            {t("currency")}
                          </TableCell>
                          <TableCell align="right">{product.notice}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" textAlign="right">
                    <strong>{t("item.fields.vat")}: </strong>
                    {(
                      quotation.products.reduce(
                        (acc, product) =>
                          acc + product.unitPrice * product.quantity,
                        0
                      ) * 0.15
                    ).toFixed(2)}{" "}
                    {t("currency")}
                  </Typography>
                  <Typography variant="body2" textAlign="right">
                    <strong>{t("orders.fields.totalPrice")}: </strong>
                    {quotation.products
                      .reduce(
                        (acc, product) =>
                          acc + product.unitPrice * product.quantity,
                        0
                      )
                      .toFixed(2)}{" "}
                    {t("currency")}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography textAlign="right">
                    <strong>{t("delivery.suggestedTime")}: </strong>
                    {quotation.deliverDays || t("general.Unavailabe")}{" "}
                    {t("time.days")}
                  </Typography>
                  <Typography textAlign="right">
                    <strong>{t("offers.validityPeriod")}: </strong>
                    {getDateLabel(quotation.DeadLineDate)}
                  </Typography>
                  {userRole === ROLE.BUYER ? (
                    <Typography textAlign="right">
                      <strong>{t("payment.method")}: </strong>
                      {quotation.paymentMethod === "1"
                        ? t("payment.direct")
                        : t("payment.deferred")}
                    </Typography>
                  ) : (
                    ""
                  )}

                  <Typography textAlign="right">
                    <strong>{t("delivery.title")}: </strong>
                    {quotation.DeliveryMethod === "FedEx"
                      ? "FedEx"
                      : quotation.DeliveryMethod === "DHL Express"
                      ? "DHL Express"
                      : quotation.DeliveryMethod === "SPL"
                      ? "سُبل"
                      : quotation.DeliveryMethod}
                  </Typography>
                  {/* <Typography textAlign="right">
                    <strong>{t("contactInfo.title")}: </strong>
                    {quotation.contactMethod === "1"
                      ? `${t("profile.fields.email")} ${
                          myCompany?.contactInfo
                        } `
                      : t("contactInfo.phone")}
                  </Typography> */}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} className="!text-gray-200 !px-3 !bg-zinc-600">
          {t("actions.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComparisonModal;
